// ─── ArtefactoWorkflowBar — componente shared cross-artefacto (ADR-025 ext 0661) ──
// Generaliza OrganigramaWorkflowBar (479 LoC) a un componente shared con adapter
// pattern. Sirve para ORGANIGRAMA, DOC, FLUJOGRAMA. Renderiza:
//   - Pill "Ed. N" (oculto si adapter.tieneVersionado === false)
//   - Badge "Etapa N/M · ACCION" (solo en EN_REVISION + etapaActual presente)
//   - <WorkflowBar> shared con acciones legales + confirm modal centralizado
//   - Botón Historial popover (oculto si !tieneVersionado)
//   - Botón "Nueva edición (Ed. N+1)" si PUBLICADO + endpointCrearBorrador
//   - Placeholder "Crear borrador" si !version + endpointCrearBorrador
//
// Wrappers ultra-finos (~50 LoC) inyectan el adapter específico por artefacto.

import { useEffect, useState, useRef } from 'react';
import { useI18n } from '../../../i18n';
import { WorkflowBar, type EstadoWorkflow } from './WorkflowBar';
import { useArtefactoVersiones } from '../hooks/useArtefactoVersiones';

// =============================================================================
// Tipos públicos
// =============================================================================

export type ArtefactoTipo = 'ORGANIGRAMA' | 'DOC' | 'FLUJOGRAMA';

export interface ArtefactoVersion {
  id: string;
  numeroEdicion: number;
  estado: EstadoWorkflow | 'DESCARTADO' | 'ARCHIVADO';
  // 0665: asunto+alcance canónicos cross-artefacto. descripcionCambios queda hasta cleanup.
  asunto: string | null;
  alcance: string | null;
  descripcionCambios: string | null;
  creadoPor: string;
  creadoEn: string;
  publicadoEn?: string | null;
}

export interface ArtefactoEtapaActual {
  orden: number;
  posicion: number;
  accion: string;
  totalEtapas: number;
}

export interface ArtefactoAdapter {
  artefactoTipo: ArtefactoTipo;
  /** GET endpoint que devuelve { data: ArtefactoVersion[], meta?: { total } } */
  endpointListVersiones: string;
  /** Construye endpoint POST /transitar dado versionId */
  buildEndpointTransitar: (versionId: string) => string;
  /** Construye endpoint GET /workflow/etapa-actual/:tipo/:id dado versionId */
  buildEndpointEtapaActual: (versionId: string) => string;
  /** Body POST para crear borrador (recibe descripción, devuelve payload JSON) */
  buildCrearBorradorPayload: (descripcion: string) => Record<string, unknown>;
  /** Endpoint POST para crear borrador. Si undefined, ocultar botón "Nueva edición" + placeholder */
  endpointCrearBorrador?: string;
  /** Si false, oculta pill "Ed. N" + historial popover (artefacto sin versionado clásico) */
  tieneVersionado?: boolean;
}

export interface ArtefactoWorkflowBarProps {
  adapter: ArtefactoAdapter;
  /** Si false, oculta el componente entero. Default true. */
  enabled?: boolean;
  /** Callback con el estado de la versión activa cada vez que cambia. */
  onVersionStateChange?: (state: EstadoWorkflow | null) => void;
  /** Callback al click en una versión del popover historial. */
  onSelectVersion?: (versionId: string, numeroEdicion: number) => void;
  /** Renderizar WorkflowBar compacto (solo iconos). Default false. */
  compact?: boolean;
  /**
   * 0670 — Si true, fuerza vista solo-vigente: oculta WorkflowBar (acciones)
   * y botón "Crear borrador", muestra solo el pill Ed.N + estado + historial.
   * Usado para roles sin participación en workflow (viewer puro).
   */
  soloVigente?: boolean;
}

// =============================================================================
// Estilos
// =============================================================================

const ESTADO_COLOR: Record<string, { bg: string; fg: string }> = {
  BORRADOR: { bg: '#E2E8F0', fg: '#4A5568' },
  EN_REVISION: { bg: '#FEF3C7', fg: '#92400E' },
  APROBADO: { bg: '#D1FAE5', fg: '#065F46' },
  PUBLICADO: { bg: '#DBEAFE', fg: '#1E40AF' },
  RECHAZADO: { bg: '#FEE2E2', fg: '#991B1B' },
  DESCARTADO: { bg: '#F3F4F6', fg: '#6B7280' },
  ARCHIVADO: { bg: '#F3F4F6', fg: '#6B7280' },
};

const LOCALE_MAP: Record<string, string> = {
  es: 'es-ES',
  en: 'en-US',
  zh: 'zh-CN',
};

function formatFecha(iso: string): string {
  const locale =
    typeof window !== 'undefined'
      ? (LOCALE_MAP[window.localStorage.getItem('vianet-locale') ?? 'es'] ?? 'es-ES')
      : 'es-ES';
  return new Date(iso).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// =============================================================================
// Componente
// =============================================================================

export function ArtefactoWorkflowBar({
  adapter,
  enabled = true,
  onVersionStateChange,
  onSelectVersion,
  compact = true,
  soloVigente = false,
}: ArtefactoWorkflowBarProps) {
  const { t } = useI18n();
  const {
    version,
    todasVersiones,
    etapaActual,
    loading,
    creating,
    mostrandoVigentePorFallback,
    cargarVersiones,
    crearBorrador,
  } = useArtefactoVersiones(adapter, {
    enabled,
    soloVigente,
    formatCrearBorradorError: (msg) => t.workflow.bar.errorCreatingDraft(msg),
  });

  const [historialOpen, setHistorialOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const tieneVersionado = adapter.tieneVersionado !== false;

  // Emitir cambio de estado al padre
  useEffect(() => {
    onVersionStateChange?.((version?.estado as EstadoWorkflow | undefined) ?? null);
  }, [version, onVersionStateChange]);

  // Cerrar popover al click fuera
  useEffect(() => {
    if (!historialOpen) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setHistorialOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [historialOpen]);

  if (!enabled) return null;

  if (loading) {
    return (
      <span style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>
        {t.workflow.bar.loading}
      </span>
    );
  }

  // Placeholder: crear primera versión (solo si endpointCrearBorrador y tieneVersionado).
  // 0670 — en modo soloVigente NO renderizamos crearBorrador (usuario sin permisos).
  if (!version && adapter.endpointCrearBorrador && tieneVersionado && !soloVigente) {
    return (
      <button
        type="button"
        disabled={creating}
        onClick={() => crearBorrador(t.workflow.bar.firstEditionDescription)}
        style={{
          padding: '6px 12px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          background: '#fff',
          color: '#1F3864',
          border: '1px solid #1F3864',
          cursor: creating ? 'not-allowed' : 'pointer',
        }}
      >
        {creating ? '…' : t.workflow.bar.createFirstDraft}
      </button>
    );
  }

  // Sin versión y sin posibilidad de crear: nada que renderizar
  if (!version) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        position: 'relative',
      }}
    >
      {/* Pill Ed. N — solo si artefacto tiene versionado clásico */}
      {tieneVersionado && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 700,
            background: '#1F3864',
            color: '#fff',
            letterSpacing: 0.3,
          }}
          title={t.workflow.bar.editionTooltip(version.numeroEdicion)}
          aria-label={`edicion-${version.numeroEdicion}`}
        >
          Ed. {version.numeroEdicion}
        </span>
      )}

      {/* Badge etapa multi-etapa — solo en EN_REVISION + etapaActual presente */}
      {etapaActual && version.estado === 'EN_REVISION' && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 700,
            background: '#FEF3C7',
            color: '#92400E',
            border: '1px solid #F59E0B',
            letterSpacing: 0.3,
          }}
          title={t.workflow.bar.stageTooltip(
            etapaActual.posicion,
            etapaActual.totalEtapas,
            etapaActual.accion,
          )}
          aria-label={`etapa-${etapaActual.posicion}-de-${etapaActual.totalEtapas}`}
        >
          {t.workflow.bar.stageBadge(
            etapaActual.posicion,
            etapaActual.totalEtapas,
            etapaActual.accion,
          )}
        </span>
      )}

      {/* 0670 — Etiqueta "última edición rechazada · mostrando vigente".
          Aparece cuando la versión más reciente quedó RECHAZADA/DESCARTADA y
          el hook cayó a la última PUBLICADO. */}
      {mostrandoVigentePorFallback && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            background: '#FEE2E2',
            color: '#991B1B',
            border: '1px solid #FCA5A5',
            letterSpacing: 0.3,
          }}
          title={t.workflow.bar.fallbackVigenteTooltip}
          aria-label="fallback-vigente"
        >
          {t.workflow.bar.fallbackVigenteBadge}
        </span>
      )}

      {/* 0670 — Pill solo-lectura cuando el usuario no participa en el flujo. */}
      {soloVigente && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            background: '#DBEAFE',
            color: '#1E40AF',
            border: '1px solid #93C5FD',
            letterSpacing: 0.3,
          }}
          title={t.workflow.bar.soloVigenteTooltip}
          aria-label="solo-vigente"
        >
          {t.workflow.bar.soloVigenteBadge}
        </span>
      )}

      {/* WorkflowBar shared con acciones legales + confirm modal.
          0670 — Si soloVigente o fallback, NO renderizar acciones (usuario
          sin permisos / versión publicada inmutable). */}
      {!soloVigente && !mostrandoVigentePorFallback && (
        <WorkflowBar
          artefactoTipo={adapter.artefactoTipo}
          artefactoId={version.id}
          estadoActual={version.estado as EstadoWorkflow}
          endpointTransitar={adapter.buildEndpointTransitar(version.id)}
          onTransicionOk={() => {
            cargarVersiones();
          }}
          compact={compact}
        />
      )}

      {/* Botón Historial — solo si tieneVersionado */}
      {tieneVersionado && (
        <button
          type="button"
          onClick={() => setHistorialOpen((s) => !s)}
          title={t.workflow.bar.viewHistory}
          aria-label={t.workflow.bar.viewHistory}
          style={{
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: '#fff',
            color: '#1F3864',
            border: '1px solid #CBD5E0',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          {t.workflow.bar.historyButton(todasVersiones.length)}
        </button>
      )}

      {/* Botón "Nueva edición (Ed. N+1)" — solo si PUBLICADO + endpointCrearBorrador */}
      {version.estado === 'PUBLICADO' && adapter.endpointCrearBorrador && tieneVersionado && (
        <button
          type="button"
          disabled={creating}
          onClick={() =>
            crearBorrador(t.workflow.bar.newEditionDescription(version.numeroEdicion + 1))
          }
          style={{
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: '#fff',
            color: '#1F3864',
            border: '1px solid #1F3864',
            cursor: creating ? 'not-allowed' : 'pointer',
          }}
        >
          {creating ? '…' : t.workflow.bar.newEditionButton(version.numeroEdicion + 1)}
        </button>
      )}

      {/* Popover historial */}
      {historialOpen && tieneVersionado && (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 380,
            maxHeight: 480,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            boxShadow: '0 10px 30px rgba(15,23,42,0.18)',
            zIndex: 50,
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#F8FAFC',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1F3864' }}>
              {t.workflow.bar.historyVersions}
            </span>
            <button
              type="button"
              onClick={() => setHistorialOpen(false)}
              aria-label={t.workflow.bar.close}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6B7280',
                fontSize: 18,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
          <div>
            {todasVersiones.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
                {t.workflow.bar.noVersionsYet}
              </div>
            ) : (
              todasVersiones
                .slice()
                .sort((a, b) => b.numeroEdicion - a.numeroEdicion)
                .map((v) => {
                  const colors = ESTADO_COLOR[v.estado] ?? ESTADO_COLOR.BORRADOR;
                  const esActiva = v.id === version.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => {
                        if (onSelectVersion) {
                          onSelectVersion(v.id, v.numeroEdicion);
                          setHistorialOpen(false);
                        }
                      }}
                      role={onSelectVersion ? 'button' : undefined}
                      tabIndex={onSelectVersion ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (onSelectVersion && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          onSelectVersion(v.id, v.numeroEdicion);
                          setHistorialOpen(false);
                        }
                      }}
                      title={
                        onSelectVersion
                          ? t.workflow.bar.viewEditionTooltip(v.numeroEdicion)
                          : undefined
                      }
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid #F1F5F9',
                        background: esActiva ? '#F0F7FF' : '#fff',
                        borderLeft: esActiva ? '3px solid #2F5496' : '3px solid transparent',
                        cursor: onSelectVersion ? 'pointer' : 'default',
                        transition: 'background 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        if (onSelectVersion && !esActiva) {
                          (e.currentTarget as HTMLElement).style.background = '#F8FAFC';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (onSelectVersion && !esActiva) {
                          (e.currentTarget as HTMLElement).style.background = '#fff';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1F3864' }}>
                          Ed. {v.numeroEdicion}
                        </span>
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 10,
                            fontSize: 10,
                            fontWeight: 700,
                            background: colors.bg,
                            color: colors.fg,
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          {t.workflow.bar.estados[
                            v.estado as keyof typeof t.workflow.bar.estados
                          ] ?? v.estado.replace('_', ' ')}
                        </span>
                        {esActiva && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: '#2F5496',
                              fontStyle: 'italic',
                            }}
                          >
                            ({t.workflow.bar.onScreen})
                          </span>
                        )}
                        <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 'auto' }}>
                          {formatFecha(v.creadoEn)}
                        </span>
                      </div>
                      {/* 0665: prioriza asunto (cross-artefacto canónico),
                          fallback a descripcionCambios legacy. Si hay alcance
                          también, lo añade en gris menor abajo. */}
                      {(v.asunto || v.descripcionCambios) && (
                        <div
                          style={{
                            fontSize: 11,
                            color: '#1F3864',
                            marginTop: 4,
                            fontWeight: v.asunto ? 600 : 400,
                          }}
                        >
                          {v.asunto ?? v.descripcionCambios}
                        </div>
                      )}
                      {v.alcance && v.asunto && (
                        <div
                          style={{
                            fontSize: 10,
                            color: '#6B7280',
                            marginTop: 2,
                            lineHeight: 1.4,
                          }}
                        >
                          {v.alcance.length > 120 ? `${v.alcance.slice(0, 120)}…` : v.alcance}
                        </div>
                      )}
                      {v.publicadoEn && (
                        <div
                          style={{
                            fontSize: 10,
                            color: '#9CA3AF',
                            marginTop: 2,
                            fontStyle: 'italic',
                          }}
                        >
                          {t.workflow.bar.publishedAt}: {formatFecha(v.publicadoEn)}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
